import { InfoCircleFilled, Lock } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors, typographyCss } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { ResponsiveTable } from 'modules/experience-core';

import { CampaignColumnIds } from './types';
import { useCampaignModuleContext } from '../../context';
import useCampaignsOptions from '../../hooks/useCampaignsOptions';
import { useSalesAndOrders } from '../../hooks/useSalesAndOrders';
import { CampaignListItem, CampaignPaidBy, PaginationProps } from '../../types';
import { getIsCampaignReadable } from '../../utils/getCampaignActions';
import CampaignActionsPopover from '../CampaignActionsPopover';
import CampaignStatusTag from '../CampaignStatusTag';
import { MultiLineTooltip, MultiLineTooltipContent } from '../MultiLineTooltip';
import Tooltip from '../Tooltip';

type BudgetDuration = 'weekly' | 'lifetime';

const getBudgetDurationFromCampaignType = (campaignType: CampaignType): BudgetDuration => {
  if (campaignType === CampaignType.ADS) {
    return 'weekly';
  }
  return 'lifetime';
};

type CampaignTableProps = {
  campaignTypesAvailableToEdit: CampaignType[];
  paginatedCampaignsList: CampaignListItem[];
  country: Country;
  sortBy: CampaignColumnIds;
  reverseSort: boolean;
  handleColumnSortChange: (columnId: CampaignColumnIds, direction: 'ascend' | 'descend') => void;
  onCampaignClick: (listItemKey: string) => void;
  onEditCampaign: (campaignId: string, campaignType: CampaignType) => void;
  onCloneCampaign: (campaignId: string) => void;
  onDeleteCampaign: (campaignId: string) => void;
  onTerminateCampaign: (campaignId: string) => void;
  onStopCampaign: (campaignId: string) => void;
  onRestartCampaign: (campaignId: string) => void;
} & PaginationProps;

const CampaignTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;
`;

const CampaignTypeText = styled.span<{ isDisabled: boolean }>`
  color: ${({ isDisabled }) => (isDisabled ? cbSemanticCssVars.textSecondary : cbSemanticCssVars.textPrimary)};
  font-weight: 500;
`;

const BudgetText = styled.span`
  color: ${cbSemanticCssVars.textSecondary};
`;

const StyledWarningInfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  fill: ${colors.banana96};
`;

const StyledInformationIcon = styled(InfoCircleFilled).attrs({ width: '12px' })`
  fill: ${cssThemedValue({ light: colors.pepper24, dark: colors.salt72 })};
`;

const StyledLockIcon = styled(Lock).attrs({ width: '12px' })`
  fill: ${cssThemedValue({ light: colors.nightSky100, dark: colors.salt72 })};
`;

const CenteredItems = styled.div`
  display: flex;
  align-items: center;
`;

const InfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  fill: ${cssThemedValue({ light: colors.pepper24, dark: colors.salt28 })};
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 10px;
  max-width: 400px;
  max-height: 600px;
  overflow-y: auto;

  ${typographyCss.Small()};
  color: ${cbSemanticCssVars.textPrimary};
`;

const HeaderRow = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  margin-block-end: 12px;
`;

const budgetSponsorLabels: Record<CampaignPaidBy, string> = {
  WOLT: 'campaigns.budget-sponsors.wolt',
  MERCHANT: 'campaigns.budget-sponsors.merchant',
  SHARED: 'campaigns.budget-sponsors.shared',
};

const SingleVenueName = ({ name }: { name: Localization[] }) => {
  const { locale, formatMessage } = useIntl();

  // We want the venue name to occupy not much more that the static campaigns.venues-count text
  const maxVenueNameLength = Math.ceil(1.5 * formatMessage({ id: 'campaigns.venues-count' }, { count: 99 }).length);
  const localizedName = localize(name, locale);
  const isOverflows = localizedName.length > maxVenueNameLength;

  return (
    <LegacyPopover
      disabled={!isOverflows}
      content={<PopoverContent>{localizedName}</PopoverContent>}
      disableContentPadding
    >
      {isOverflows ? localizedName.substring(0, maxVenueNameLength) + '...' : localizedName}
    </LegacyPopover>
  );
};

const VenueCell = ({ record, merchantVenues }: { record: CampaignListItem; merchantVenues: Venue[] }) => {
  const { locale } = useIntl();

  const filteredVenueIds = record.venueIds.filter(venueId =>
    merchantVenues.some(merchantVenue => merchantVenue.id === venueId),
  );

  const singleVenueName =
    filteredVenueIds.length === 1 && merchantVenues.find(venue => venue.id === filteredVenueIds[0])?.name;

  if (singleVenueName) {
    return <SingleVenueName name={singleVenueName} />;
  }

  return (
    <CenteredItems>
      <FormattedMessage id="campaigns.venues-count" values={{ count: filteredVenueIds.length }} />
      {filteredVenueIds.length > 0 && (
        <LegacyPopover
          content={
            <PopoverContent>
              {filteredVenueIds.map(venueId => (
                <div key={venueId}>
                  {localize(merchantVenues.find(venue => venue.id === venueId)?.name ?? [], locale)}
                </div>
              ))}
            </PopoverContent>
          }
          disableContentPadding
        >
          &nbsp;
          <InfoIcon />
        </LegacyPopover>
      )}
    </CenteredItems>
  );
};

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Amount = styled.span`
  color: ${cbSemanticCssVars.textPrimary};
`;

const RestrictedBudgetMessage = styled.span`
  color: ${cbSemanticCssVars.textSecondary};
`;
const CampaignTable = ({
  campaignTypesAvailableToEdit = [],
  paginatedCampaignsList,
  pageNumber,
  pageSize,
  totalPages,
  sortBy,
  reverseSort,
  handleColumnSortChange,
  setPageNumber,
  setPageSize,
  onCampaignClick,
  onEditCampaign,
  onCloneCampaign,
  onDeleteCampaign,
  onTerminateCampaign,
  onStopCampaign,
  onRestartCampaign,
}: CampaignTableProps) => {
  const { currency, entityType, venues, merchantId } = useCampaignModuleContext();
  const { formatMessage, locale } = useIntl();
  const { getCampaignTitleByType } = useCampaignsOptions();
  const isMerchantLevel = entityType === 'merchant';

  const entityId = isMerchantLevel ? merchantId : venues[0].id;

  const salesAndOrders = useSalesAndOrders({
    entityType,
    entityId,
    campaignsList: paginatedCampaignsList,
  });
  const salesAndOrdersWeekly = useSalesAndOrders({
    entityType,
    entityId,
    campaignsList: paginatedCampaignsList.filter(
      campaign => getBudgetDurationFromCampaignType(campaign.campaignType) === 'weekly',
    ),
    duration: { days: 7 },
  });

  const shouldDisplayBudgetAndSpend = (record: CampaignListItem) =>
    isMerchantLevel
      ? record.venueIds.every(venueId => venues.some(merchantVenue => merchantVenue.id === venueId))
      : record.venueIds.length === 1;

  return (
    <div>
      <HeaderRow>
        <T.Heading6>All campaigns</T.Heading6>
      </HeaderRow>
      <CampaignTableContainer>
        <ResponsiveTable<CampaignListItem>
          horizontalPadding="wide"
          verticalSpacing="spacious"
          dataSource={paginatedCampaignsList}
          getRowKey={record => `${record.campaignId}_${record.campaignType}`}
          roundedCorners
          onRowClick={rowKey => onCampaignClick(rowKey)}
          isRowClickable={record =>
            getIsCampaignReadable({
              campaignType: record.campaignType,
              creationSource: record.creationSource,
              isCreatedByWolt: record.isCreatedByWolt,
            })
          }
          sortedColumn={{
            id: sortBy,
            direction: reverseSort ? 'descend' : 'ascend',
          }}
          pagination={{
            activePage: pageNumber,
            disableWhenLoading: true,
            pageSize,
            pageCount: totalPages,
            onPageChange: (pageNumber: number) => setPageNumber(pageNumber),
            onPageSizeChange: (pageSize: number) => setPageSize(pageSize),
          }}
          onColumnSortChange={(columnId, direction) => handleColumnSortChange(columnId as CampaignColumnIds, direction)}
          columns={[
            {
              id: CampaignColumnIds.CAMPAIGN_TYPE,
              sortDirections: ['ascend', 'descend'],
              name: formatMessage({ id: 'campaigns.labels.type' }),
              render: ({ record }) => (
                <CampaignTypeText
                  isDisabled={
                    !getIsCampaignReadable({
                      campaignType: record.campaignType,
                      creationSource: record.creationSource,
                      isCreatedByWolt: record.isCreatedByWolt,
                    })
                  }
                >
                  {getCampaignTitleByType(record.campaignType, record.creationSource, record.isAdFreeTrial)}{' '}
                  {!isMerchantLevel && record.venueIds.length > 1 && (
                    <Tooltip
                      icon={<StyledLockIcon />}
                      description={<FormattedMessage id="campaigns.error-multi-venue-campaign" />}
                    />
                  )}
                </CampaignTypeText>
              ),
            },
            {
              id: CampaignColumnIds.PAID_BY,
              name: formatMessage({ id: 'campaigns.labels.paid-by' }),
              sortDirections: ['ascend', 'descend'],
              render: ({ record }) => (
                <>
                  {record.paidBy ? (
                    <Tag variant="lightBlack">
                      <FormattedMessage id={budgetSponsorLabels[record.paidBy]} />
                    </Tag>
                  ) : (
                    <FormattedMessage id="campaigns.budget-sponsors.unknown" />
                  )}
                </>
              ),
            },
            ...(isMerchantLevel
              ? [
                  {
                    id: CampaignColumnIds.VENUE,
                    name: formatMessage({ id: 'campaigns.labels.venue' }),
                    render: ({ record }: { record: CampaignListItem }) => (
                      <VenueCell
                        record={record}
                        merchantVenues={venues.filter(merchantVenue => record.venueIds.includes(merchantVenue.id))}
                      />
                    ),
                  },
                ]
              : []),
            {
              id: CampaignColumnIds.START_DATE,
              name: formatMessage({ id: 'campaigns.labels.start-date' }),
              sortDirections: ['ascend', 'descend'],
              render: ({ record }) =>
                DateTime.fromISO(record.startDate).toLocaleString(DateTime.DATETIME_SHORT, { locale }),
            },
            {
              id: CampaignColumnIds.END_DATE,
              sortDirections: ['ascend', 'descend'],
              name: formatMessage({ id: 'campaigns.labels.end-date' }),
              render: ({ record }) =>
                record.endDate
                  ? (record.campaignType === CampaignType.ADS
                      ? DateTime.fromISO(record.endDate).endOf('day')
                      : DateTime.fromISO(record.endDate)
                    ).toLocaleString(DateTime.DATETIME_SHORT, {
                      locale,
                    })
                  : '-',
            },
            {
              id: CampaignColumnIds.ORDERS,
              name: formatMessage({ id: 'campaigns.labels.orders' }),
              sortDirections: ['ascend', 'descend'],
              render: ({ record }) => {
                const isWeeklyBudget = getBudgetDurationFromCampaignType(record.campaignType) === 'weekly';

                const weeklyOrders = salesAndOrdersWeekly.data?.[record.campaignId]?.orders ?? 0;
                const totalOrders = salesAndOrders.data?.[record.campaignId]?.orders ?? 0;

                if (salesAndOrders.isFetching || (isWeeklyBudget && salesAndOrdersWeekly.isFetching)) {
                  return (
                    <CenteredItems>
                      <Spinner size="tiny" color="grey" />
                    </CenteredItems>
                  );
                }

                return (
                  <CenteredItems>
                    {totalOrders ?? '-'}
                    {isWeeklyBudget && (
                      <MultiLineTooltip
                        icon={<StyledInformationIcon />}
                        tooltipLines={[
                          {
                            titleKey: 'campaigns.last-7-days',
                            value: <T.Small>{weeklyOrders ?? '-'}</T.Small>,
                          },
                          {
                            titleKey: 'campaigns.total-orders-since-start',
                            value: <T.Small>{totalOrders ?? '-'}</T.Small>,
                          },
                        ]}
                      />
                    )}
                  </CenteredItems>
                );
              },
            },
            {
              id: CampaignColumnIds.SALES,
              name: formatMessage({ id: 'campaigns.labels.sales' }),
              sortDirections: ['ascend', 'descend'],
              render: ({ record }) => {
                const isPopoverInfoAvailable = getBudgetDurationFromCampaignType(record.campaignType) === 'weekly';

                const weeklySales = salesAndOrdersWeekly.data?.[record.campaignId]?.sales ?? 0;
                const totalSales = salesAndOrders.data?.[record.campaignId]?.sales ?? 0;

                if (salesAndOrders.isFetching || (isPopoverInfoAvailable && salesAndOrdersWeekly.isFetching)) {
                  return (
                    <CenteredItems>
                      <Spinner size="tiny" color="grey" />
                    </CenteredItems>
                  );
                }

                return (
                  <CenteredItems>
                    {typeof totalSales === 'number' ? <MonetaryAmount amount={totalSales} currency={currency} /> : '-'}
                    {isPopoverInfoAvailable && (
                      <MultiLineTooltip
                        icon={<StyledInformationIcon />}
                        tooltipLines={[
                          {
                            titleKey: 'campaigns.last-7-days',
                            value: (
                              <T.Small>
                                <MonetaryAmount amount={weeklySales ?? 0} currency={currency} />
                              </T.Small>
                            ),
                          },
                          {
                            titleKey: 'campaigns.total-sales-since-start',
                            value: (
                              <T.Small>
                                <MonetaryAmount amount={totalSales ?? 0} currency={currency} />
                              </T.Small>
                            ),
                          },
                        ]}
                      />
                    )}
                  </CenteredItems>
                );
              },
            },
            {
              id: CampaignColumnIds.CAMPAIGN_STATUS,
              sortDirections: ['ascend', 'descend'],
              name: formatMessage({ id: 'campaigns.labels.status' }),
              render: ({ record }) => <CampaignStatusTag status={record.status} />,
            },
            {
              id: CampaignColumnIds.BUDGET,
              sortDirections: ['ascend', 'descend'],
              name: formatMessage({ id: 'campaigns.labels.budget' }),
              render: ({ record }) => (
                <CenteredItems>
                  {shouldDisplayBudgetAndSpend(record) ? (
                    <BudgetText>
                      {record.budget ? (
                        <FormattedMessage
                          id="campaigns.budget-duration"
                          values={{
                            amount: () => (
                              <Amount>
                                <MonetaryAmount amount={record.budget} currency={currency} />
                              </Amount>
                            ),
                            budgetDuration: getBudgetDurationFromCampaignType(record.campaignType),
                          }}
                        />
                      ) : (
                        <FormattedMessage id="campaigns.form.budget.unlimited" />
                      )}
                    </BudgetText>
                  ) : (
                    <>
                      <RestrictedBudgetMessage>
                        <FormattedMessage id="campaigns.budget-restricted.restricted" />
                      </RestrictedBudgetMessage>
                      <Tooltip
                        icon={<StyledInformationIcon />}
                        description={
                          <TooltipContent>
                            {isMerchantLevel ? (
                              <FormattedMessage id="campaigns.budget-restricted.no-access-to-all-venues" />
                            ) : (
                              <FormattedMessage id="campaigns.budget-restricted.multi-venue-campaign" />
                            )}
                          </TooltipContent>
                        }
                      />
                    </>
                  )}
                </CenteredItems>
              ),
            },
            {
              id: CampaignColumnIds.SPEND,
              sortDirections: ['ascend', 'descend'],
              name: formatMessage({ id: 'campaigns.labels.total-spend' }),
              render: ({ record }: { record: CampaignListItem }) => {
                const isWeeklyBudget = getBudgetDurationFromCampaignType(record.campaignType) === 'weekly';

                const { totalSpend: weeklySpend = 0 } = salesAndOrdersWeekly.data?.[record.campaignId] ?? {};

                const {
                  totalMerchantSpend = 0,
                  totalSpend = 0,
                  totalWoltSpend = 0,
                } = salesAndOrders.data?.[record.campaignId] ?? {};

                if (salesAndOrders.isFetching || (isWeeklyBudget && salesAndOrdersWeekly.isFetching)) {
                  return (
                    <CenteredItems>
                      <Spinner size="tiny" color="grey" />
                    </CenteredItems>
                  );
                }

                const isSharedBudget = record.paidBy === 'SHARED' && !!totalSpend;

                return (
                  <CenteredItems>
                    <>
                      <MonetaryAmount amount={totalSpend ?? 0} currency={currency} />
                      {(isSharedBudget || record.isBudgetExceeded || isWeeklyBudget) && (
                        // todo show a compound tooltip for weekly + shared budget, if we find out such campaigns exist
                        <Tooltip
                          icon={record.isBudgetExceeded ? <StyledWarningInfoIcon /> : <StyledInformationIcon />}
                          description={
                            <TooltipContent>
                              {record.isBudgetExceeded && (
                                <FormattedMessage id="campaigns.exceeded-budget-description" />
                              )}
                              {isWeeklyBudget ? (
                                <MultiLineTooltipContent
                                  tooltipLines={[
                                    {
                                      titleKey: 'campaigns.last-7-days',
                                      value: <MonetaryAmount amount={weeklySpend ?? 0} currency={currency} />,
                                    },
                                    {
                                      titleKey: 'campaigns.total-spend-since-start',
                                      value: <MonetaryAmount amount={totalSpend ?? 0} currency={currency} />,
                                    },
                                  ]}
                                />
                              ) : isSharedBudget ? (
                                <MultiLineTooltipContent
                                  tooltipLines={[
                                    {
                                      titleKey: 'campaigns.budget-sponsors.merchant',
                                      value: <MonetaryAmount amount={totalMerchantSpend ?? 0} currency={currency} />,
                                    },
                                    {
                                      titleKey: 'campaigns.budget-sponsors.wolt',
                                      value: <MonetaryAmount amount={totalWoltSpend ?? 0} currency={currency} />,
                                    },
                                  ]}
                                />
                              ) : null}
                            </TooltipContent>
                          }
                        />
                      )}
                    </>
                  </CenteredItems>
                );
              },
            },
            ...(campaignTypesAvailableToEdit.length > 0
              ? [
                  {
                    id: CampaignColumnIds.CAMPAIGN_ACTIONS,
                    renderName: false,
                    name: formatMessage({ id: 'translation.actions' }),
                    render: ({ record }: { record: CampaignListItem }) => {
                      if (
                        campaignTypesAvailableToEdit.includes(record.campaignType) &&
                        record.allowedActions.some(action => action !== 'read')
                      ) {
                        return (
                          <CampaignActionsPopover
                            campaignId={record.campaignId}
                            campaignType={record.campaignType}
                            allowedActions={record.allowedActions}
                            onEditCampaign={onEditCampaign}
                            onCloneCampaign={onCloneCampaign}
                            onDeleteCampaign={onDeleteCampaign}
                            onTerminateCampaign={onTerminateCampaign}
                            onRestartCampaign={onRestartCampaign}
                            onStopCampaign={onStopCampaign}
                          />
                        );
                      }
                      return null;
                    },
                  },
                ]
              : []),
          ]}
        />
      </CampaignTableContainer>
    </div>
  );
};

export default CampaignTable;
